import React from 'react';

import { useTranslation } from 'react-i18next';
import { Button, Icon } from 'design-system-web';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import { AdminPage, Loading } from 'components';
import { Alert, AlertType } from 'components/lds';
import { H3, H4, S } from 'components/typography';

import { Breadcrumbs } from '../components/inspectionTemplateCustomize/InspectionTemplateCustomizeBreadcrumbs';
import { SectionCard } from '../components/inspectionTemplateCustomize/InspectionTemplateCustomizeSectionCard';
import { InspectionTemplateCustomizeSidebar } from '../components/inspectionTemplateCustomize/InspectionTemplateCustomizeSidebar';
import { useInspectionTemplateCustomizePage } from '../hooks/inspectionTemplateCustomize/useInspectionTemplateCustomizePage';
import styles from './InspectionTemplateCustomizePage.scss';

type Props = {
  channelBasePath: string;
};

export function InspectionTemplateCustomizePage({ channelBasePath }: Props) {
  const { t } = useTranslation();
  const vm = useInspectionTemplateCustomizePage(channelBasePath);

  if (!vm.hasValidRouteContext) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.loadingOrError}>
          <Alert type={AlertType.error} fullWidth>
            {t('web.admin.inspections.templateCustomize.missingContext')}
          </Alert>
        </div>
      </AdminPage>
    );
  }

  if (vm.error) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.page}>
          <Breadcrumbs listPageUrl={vm.listPageUrl} />
          <Alert
            type={AlertType.error}
            fullWidth
            className={styles.alertSpacing}
          >
            {t('web.admin.inspections.templateCustomize.error')}
          </Alert>
          <Button variant="secondary" size="large" onClick={vm.handleCancel}>
            {t('web.admin.inspections.templateCustomize.cancel')}
          </Button>
        </div>
      </AdminPage>
    );
  }

  if ((vm.loading || !vm.called) && !vm.template) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.loadingOrError}>
          <Loading />
        </div>
      </AdminPage>
    );
  }

  if (vm.called && !vm.loading && !vm.template) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.page}>
          <Breadcrumbs listPageUrl={vm.listPageUrl} />
          <Alert
            type={AlertType.error}
            fullWidth
            className={styles.alertSpacing}
          >
            {t('web.admin.inspections.templateCustomize.notFound')}
          </Alert>
          <Button variant="secondary" size="large" onClick={vm.handleCancel}>
            {t('web.admin.inspections.templateCustomize.cancel')}
          </Button>
        </div>
      </AdminPage>
    );
  }

  if (!vm.template) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.loadingOrError}>
          <Loading />
        </div>
      </AdminPage>
    );
  }

  return (
    <AdminPage className={styles.adminPage}>
      <div className={styles.page}>
        <Breadcrumbs listPageUrl={vm.listPageUrl} />

        <div className={styles.pageTitle}>
          <H3>{vm.template.metadata.name}</H3>
          <S variant="secondary">
            {t('web.admin.inspections.templateCustomize.lastUpdated', {
              date: vm.lastUpdatedText,
            })}
          </S>
        </div>

        <div className={styles.contentRow}>
          <div ref={vm.mainColumnRefCallback} className={styles.mainColumn}>
            <div className={styles.alertWrap}>
              <Alert type={AlertType.info} fullWidth className={styles.alertBanner}>
                {t('web.admin.inspections.templateCustomize.instanceOnlyNotice')}
              </Alert>
            </div>

            <div className={styles.questionsHeadingRow}>
              <div className={styles.questionsHeadingText}>
                <H4>
                  {t('web.admin.inspections.templateCustomize.questionsHeading')}
                </H4>
                <S variant="secondary">{vm.summary}</S>
              </div>
              <span title={vm.revertAllDisabledTitle}>
                <Button
                  variant="secondary"
                  size="large"
                  disabled
                  aria-disabled
                  startIcon={
                    <Icon
                      name="undo"
                      set={ICON_SET_FONTAWESOME}
                      type="far"
                    />
                  }
                >
                  {t('web.admin.inspections.templateCustomize.revertAll')}
                </Button>
              </span>
            </div>

            {vm.sortedSections.map((section, sectionIndex) => (
              <SectionCard
                key={section.id}
                section={section}
                sectionIndex={sectionIndex}
                expanded={vm.expandedBySectionId[section.id] ?? true}
                onToggle={() => vm.toggleSection(section.id)}
                removeSectionDisabledTitle={vm.removeSectionDisabledTitle}
                removeItemDisabledTitle={vm.removeItemDisabledTitle}
              />
            ))}
          </div>

          <InspectionTemplateCustomizeSidebar
            sections={vm.sortedSections}
            activeSectionId={vm.activeSectionId}
          />
        </div>

        <div className={styles.footer}>
          <span title={vm.saveDisabledTitle}>
            <Button variant="primary" size="large" disabled aria-disabled>
              {t('web.admin.inspections.templateCustomize.save')}
            </Button>
          </span>
          <Button variant="text" size="large" onClick={vm.handleCancel}>
            {t('web.admin.inspections.templateCustomize.cancel')}
          </Button>
        </div>
      </div>
    </AdminPage>
  );
}
